/**
 * @deprecated Legacy per-batch CPU time estimation for progressive rendering when
 * USE_OUT_OF_CORE_TILE_MANAGER is off. The pre-OOC path deducts estimated milliseconds
 * per rendered batch from the tick time budget. New code should use batch-count budgets
 * (RenderBudgetManager) instead.
 */
import { MIN_BATCH_TIME_MS } from '../globals';

/** @import { RenderBatch } from "./RenderBatch" */

// Smoothing factor for the per-batch cpuAvgFrameTime EMA on the legacy (non-OOC) path.
// new = (1-a)*old + a*sample. Lower = smoother but slower to adapt (~ln(0.5)/ln(1-a) frames).
const RENDER_BATCH_TIME_EMA_ALPHA = 0.05;

/**
 * @typedef {Object} LegacyBatchTimingState
 * @property {number} batchCount
 * @property {number} batchTimeSum
 * @property {number} rejectOutliers
 */

/**
 * @returns {LegacyBatchTimingState}
 */
export function createLegacyBatchTimingState() {
  return {
    batchCount: 0,
    batchTimeSum: 0,
    rejectOutliers: 0
  };
}

/**
 * For each sub-scene, keep a running average of how long it took to render over the last few frames.
 * @param {RenderBatch|THREE.Scene} renderBatch
 * @param {number} frameTime - last measured rendering time in ms
 */
export function updateAvgFrameTime(renderBatch, frameTime) {
  if (renderBatch.cpuAvgFrameTime === undefined) {
    renderBatch.cpuAvgFrameTime = frameTime;
    renderBatch.outliers = [];
    return;
  }

  const outlier = frameTime < 0.7 * renderBatch.cpuAvgFrameTime || frameTime > 1.5 * renderBatch.cpuAvgFrameTime;
  let updateTime = true;
  if (outlier) {
    renderBatch.outliers.push(frameTime);
    updateTime = false;
    if (renderBatch.outliers.length > 4) {
      renderBatch.cpuAvgFrameTime = renderBatch.outliers.reduce((a, b) => a + b, 0) / renderBatch.outliers.length;
      renderBatch.outliers.splice(0, 1000);
    }
  } else {
    renderBatch.outliers.splice(0, 1000);
  }

  if (updateTime) {
    const a = RENDER_BATCH_TIME_EMA_ALPHA;
    renderBatch.cpuAvgFrameTime = (1 - a) * renderBatch.cpuAvgFrameTime + a * frameTime;
  }
}

/**
 * Clamp a wall-clock batch interval to the minimum measurable delta.
 * @param {number} t0
 * @param {number} t1
 * @returns {number}
 */
export function measureBatchDelta(t0, t1) {
  return Math.max(MIN_BATCH_TIME_MS, t1 - t0);
}

/**
 * Update per-batch timing estimates and return the milliseconds to deduct from timeRemaining.
 * @param {LegacyBatchTimingState} state
 * @param {RenderBatch|THREE.Scene} renderBatch
 * @param {number} measuredDelta - wall-clock ms for this batch (already clamped)
 * @param {boolean} interruptedInPreviousFrame
 * @returns {number}
 */
export function deductLegacyBatchTimeCost(state, renderBatch, measuredDelta, interruptedInPreviousFrame) {
  if (interruptedInPreviousFrame) {
    return measuredDelta;
  }

  let delta = measuredDelta;

  // We exclude large outliers from the render batch average computation. The reason for this is
  // that the renderer sometimes blocks for a long time in a single render batch, if its queue is
  // full. If we add this time to that batch, we would completely miss estimate the price for this
  // individual batch. Instead, we only keep its average time. This way, the overall frame time
  // estimation might be too small, but it will not have strong outliers. We instead rely on the
  // frame time budget computation to compensate for this underestimation.
  let batchTimeAverage;
  if (state.batchCount > 5) {
    batchTimeAverage = state.batchTimeSum / state.batchCount;
    if (delta > 5 * batchTimeAverage && state.rejectOutliers < 3) {
      delta = renderBatch.cpuAvgFrameTime ?? batchTimeAverage;
      state.rejectOutliers++;
    }
  }
  state.batchTimeSum += delta;
  state.batchCount++;

  // Cold-path estimate: batch has no CPU history yet. Fall back to the tick average, or a
  // fixed 0.5ms constant when no batch history is available.
  if (renderBatch.cpuAvgFrameTime === undefined) {
    delta = batchTimeAverage ?? 0.5;
  }

  updateAvgFrameTime(renderBatch, delta);

  // Note that we don't do accurate timing here, but compute with average values instead.
  // In this way, the number of rendered batches is more consistent across different frames.
  return renderBatch.cpuAvgFrameTime;
}